import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import InlineTextEditor from '../InlineTextEditor';

const TextBlock = ({ data, isSelected, onUpdate, previewMode, blockId, onSelectBlock }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(data.content || '');
  const [showInlineEditor, setShowInlineEditor] = useState(false);
  const [editorPosition, setEditorPosition] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const textareaRef = useRef(null);
  const textRef = useRef(null);
  
  const {
    content = 'Your text here...',
    fontSize = 16,
    color = '#333333',
    backgroundColor = 'transparent',
    fontWeight = 'normal',
    fontStyle = 'normal',
    textDecoration = 'none',
    textAlign = 'left',
    lineHeight = 1.5,
    fontFamily = 'Arial, sans-serif',
    marginTop = 0,
    marginBottom = 15,
    padding = 10,
  } = data;

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  // Синхронизируем содержимое при изменении данных извне
  useEffect(() => {
    if (!isEditing) {
      setEditContent(content);
    }
  }, [content, isEditing]);

  const getResponsiveFontSize = () => {
    switch (previewMode) {
      case 'mobile':
        return Math.max(fontSize * 0.9, 14);
      case 'tablet':
        return Math.max(fontSize * 0.95, 15);
      default:
        return fontSize;
    }
  };

  const blockStyles = {
    fontSize: `${getResponsiveFontSize()}px`,
    color,
    backgroundColor,
    fontWeight,
    fontStyle,
    textDecoration,
    textAlign,
    lineHeight,
    fontFamily,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    padding: `${padding}px`,
    border: isSelected ? '1px dashed #007bff' : 'none',
    borderRadius: isSelected ? '4px' : 0,
    cursor: 'text',
    minHeight: '20px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setEditContent(content);
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (onUpdate && editContent !== content) {
      onUpdate({ content: editContent });
      
      // Обновляем selectedBlock если этот блок выбран
      if (onSelectBlock && blockId) {
        onSelectBlock({
          id: blockId,
          type: 'text',
          data: { ...data, content: editContent }
        });
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      setEditContent(content);
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && selection.toString().length > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      setSelectedText(selection.toString());
      setEditorPosition({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
      setShowInlineEditor(true);
    } else {
      setShowInlineEditor(false);
    }
  };

  const handleInlineFormat = (property, value) => {
    if (onUpdate) {
      onUpdate({ [property]: value });
    }
  };

  const handleAddLink = (url) => {
    // For now, we'll just update the content with a simple link
    // In a more advanced implementation, we'd wrap only the selected text
    const linkText = selectedText || 'Link';
    const newContent = content.replace(selectedText, `<a href="${url}">${linkText}</a>`);
    if (onUpdate) {
      onUpdate({ content: newContent });
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ ...blockStyles, padding: 0 }}>
        <textarea
          ref={textareaRef}
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            minHeight: '60px',
            border: 'none',
            outline: 'none',
            resize: 'vertical',
            fontSize: `${getResponsiveFontSize()}px`,
            color,
            fontWeight,
            fontStyle,
            textDecoration,
            textAlign,
            lineHeight,
            fontFamily,
            padding: `${padding}px`,
            backgroundColor: 'transparent',
          }}
          placeholder="Enter your text here..."
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        ref={textRef}
        sx={blockStyles}
        onDoubleClick={handleDoubleClick}
        onMouseUp={handleTextSelection}
        onTouchEnd={handleTextSelection}
      >
        {content || 'Double-click to edit text...'}
      </Box>
      
      <InlineTextEditor
        isVisible={showInlineEditor && !isEditing}
        position={editorPosition}
        selectedText={selectedText}
        onFormat={handleInlineFormat}
        onAddLink={handleAddLink}
        onClose={() => setShowInlineEditor(false)}
      />
    </>
  );
};

export default TextBlock; 